import React, {useState} from 'react';
import {Layout, Tag} from "antd";
import {Button as NButton, Card, Container, Text} from "@nextui-org/react";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, doc, getFirestore, query, serverTimestamp, Timestamp, updateDoc, where} from "firebase/firestore";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import Firebase from "../../Firebase/Firebase";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";

const db = getFirestore(Firebase);
const {Content} = Layout;

function numberWithCommas(x: string | number) {
    return String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtDate(val: any) {
    if (!val) return '—';
    const d = typeof val.toDate === 'function' ? val.toDate() : val;
    return moment(d).format('DD MMM YYYY, HH:mm');
}

// Sellers who've exhausted their 3 self-serve extensions can ask Shopped to
// broker a further request on their behalf (out-of-band — email/chat, not a
// form in-app). Admin never grants time directly: this writes the exact same
// pending-request fields a self-serve request would, so the buyer sees the
// same accept/deny banner and still has to consent. See the Taking Orders
// plan, admin-brokered extension section.
const BrokerRow = ({order}: {order: any}) => {
    const toast = useToast();
    const [open, setOpen] = useState(false);
    const [days, setDays] = useState('7');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const alreadyPending = order.extensionStatus === 'pending';

    const submit = async () => {
        const n = parseInt(days, 10);
        if (!n || n <= 0) {
            toast({position: 'top', status: 'error', title: 'Enter a valid number of days'});
            return;
        }
        setSubmitting(true);
        try {
            await updateDoc(doc(db, 'orders', order.orderId), {
                extensionStatus: 'pending',
                extensionRequestedDays: n,
                extensionRequestReason: reason.trim() || null,
                extensionRequestedAt: serverTimestamp(),
                extensionRespondBy: Timestamp.fromMillis(Date.now() + 72 * 60 * 60 * 1000),
                extensionViaAdmin: true,
            });
            toast({position: 'top', status: 'success', title: 'Extension request sent to the buyer'});
            setOpen(false);
            setReason('');
        } catch (e: any) {
            toast({position: 'top', status: 'error', title: 'Failed to send request', description: e?.message});
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{border: '1px solid #eee', borderRadius: 8, padding: '12px 16px', background: '#fff'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <div>
                    <div style={{fontWeight: 600, fontSize: 14}}>Order #{order.orderNo ?? order.orderId}</div>
                    <div style={{fontSize: 12, color: '#888'}}>
                        Deadline: {fmtDate(order.preorderDeadline)} · {order.extensionsUsed ?? 0} extensions used
                    </div>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                    <span style={{fontSize: 13}}>MWK {numberWithCommas(order.totalAmount ?? 0)}</span>
                    {alreadyPending ? (
                        <Tag color="gold">Request pending buyer</Tag>
                    ) : (
                        <NButton size="sm" color="primary" onClick={() => setOpen((o) => !o)}>
                            {open ? 'Cancel' : 'Broker extension'}
                        </NButton>
                    )}
                </div>
            </div>
            {open && !alreadyPending && (
                <div style={{marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end'}}>
                    <div>
                        <div style={{fontSize: 11, color: '#888', marginBottom: 4}}>Additional days</div>
                        <input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)}
                            style={{width: 80, padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6}} />
                    </div>
                    <div style={{flex: 1, minWidth: 200}}>
                        <div style={{fontSize: 11, color: '#888', marginBottom: 4}}>Note (optional, shown to the buyer)</div>
                        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Customs delay confirmed by seller"
                            style={{width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6}} />
                    </div>
                    <NButton size="sm" color="success" onClick={submit} disabled={submitting}>
                        {submitting ? 'Sending…' : 'Send to buyer'}
                    </NButton>
                </div>
            )}
        </div>
    );
};

const PreorderExtensions = () => {
    const [collapsed, setCollapsed] = useState(false);

    // Composite filter (isPreorder + extensionsUsed range) needs a Firestore
    // composite index — same pattern as the other admin queries in this app.
    const [snapshot, loading] = useCollection(
        query(
            collection(db, 'orders'),
            where('isPreorder', '==', true),
            where('extensionsUsed', '>=', 3),
        ),
    );

    const rows = (snapshot?.docs ?? [])
        .map((d) => ({orderId: d.id, ...d.data()} as any))
        .filter((o) => (o.orderStatus ?? '').toLowerCase() === 'pending')
        .sort((a, b) => (a.preorderDeadline?.toMillis?.() ?? 0) - (b.preorderDeadline?.toMillis?.() ?? 0));

    return (
        <Layout style={{minHeight: '100vh'}}>
            <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
            <Layout className="site-layout">
                <Sidebar collapsed={collapsed}/>
                <Content>
                    <Container className="mt-2">
                        <Card className="my-2 p-3 font-JosefinSans">
                            <Text h4 size={23} css={{color: "#29327b", fontWeight: "$bold"}}>
                                Taking Orders — Extension Requests
                            </Text>
                            <Text size={13} css={{color: "#888", marginBottom: 12}}>
                                Sellers who&apos;ve used all 3 self-serve extensions and still need more time. Broker
                                a request on their behalf — the buyer still has to accept it, same as a normal
                                extension request.
                            </Text>

                            {loading ? (
                                <div style={{padding: 40, textAlign: 'center', color: '#999'}}>Loading…</div>
                            ) : rows.length === 0 ? (
                                <div style={{padding: 40, textAlign: 'center', color: '#999'}}>
                                    No sellers currently need a brokered extension.
                                </div>
                            ) : (
                                <div style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
                                    {rows.map((order) => (
                                        <BrokerRow key={order.orderId} order={order} />
                                    ))}
                                </div>
                            )}
                        </Card>
                    </Container>
                </Content>
            </Layout>
        </Layout>
    );
};

export default PreorderExtensions;
